/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000224 配送ルート番号照会
 */
import { Column, Query } from "@material-table/core";
import { B000224Find, B000224FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SFrame,
  SLabel,
  SNormalButton,
  STable,
  SText,
  STextField,
} from "components";
import {} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  batchNo: string;
  haisorouteNoFrom: string;
  haisorouteNoTo: string;
  name: string;
}>;
// テーブルデータ定義
type Data = Partial<{
  gyo: number;
  batchNo: number;
  haisorouteNo: number;
  haisorouteNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  batchNo: "",
  haisorouteNoFrom: "",
  haisorouteNoTo: "",
  name: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "3rem", align: "right" },
  { field: "batchNo", title: "バッチ番号", width: "5rem" },
  { field: "haisorouteNo", title: "配送ルート番号", width: "5rem" },
  { field: "haisorouteNm", title: "配送ルート名称", width: "10rem" },
];

const focusId = "B000224";

// B000224 配送ルート番号照会
export const B000224 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000224FindRequest = {
        batchNo: fields.batchNo,
        haisorouteNoFrom: fields.haisorouteNoFrom,
        haisorouteNoTo: fields.haisorouteNoTo,
        name: fields.name,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        sort: ["batchNo,asc"],
      };
      // API呼出し
      B000224Find(param)
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="配送ルート番号照会"
        gridTemplateColumns="7rem 4rem 3rem 1rem 7rem 10rem 1rem 1rem"
      >
        <SLabel>バッチ番号</SLabel>
        <STextField name="batchNo" gridColumn="2" size={4} {...register(1)} />
        <SLabel gridColumn={1}>配送ルート番号</SLabel>
        <STextField
          name="haisorouteNoFrom"
          gridColumn="span 2"
          size={9}
          {...register(2)}
        />
        <SText>～</SText>
        <STextField
          name="haisorouteNoTo"
          gridColumn="5"
          size={9}
          {...register(3)}
        />
        <SLabel gridColumn={1}>配送ルート名</SLabel>
        <STextField
          name="name"
          size={40}
          gridColumn="span 5"
          {...register(4)}
        />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          {...register(6)}
          gridColumn="8"
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          tableRef={tableRef}
          columns={columns}
          handleDoubleClick={handleDoubleClickTableRow}
          width="48rem"
        />
      </SFrame>
    </FormProvider>
  );
};
